// ---------------------------------------------------------------------------
// The products and stages that the selectors offer, after the stage rules of
// an admin (`hiddenProducts`, `hiddenStageProducts`, `shownStageProducts`,
// libs/selectorVisibility.ts).
//
// The stages of a product are the ones that the code gives it, and on a
// stage-typed channel also the ones an admin put it on (stagesWithProduct).
//
// A product is hidden in the whole channel or on single stages. The Product
// dropdown of a stage-typed channel (Releases) offers the whole channel, so
// a product stays in it while one of its stages offers it, and a pick moves
// to such a stage. A content-type channel offers the products of the current
// type, so a product hidden on that type leaves the dropdown.
// ---------------------------------------------------------------------------

import {findChannelObj, isContentTypeSelectable, productCodesForType} from "../data/products.js";
import {
  isProductOffered,
  offeredStages,
  stageHasProduct,
  type ChannelSettingsLike,
} from "./selectorVisibility";

/**
 * The stages (content types) of the channel that have `product`, in declared
 * order: the ones the code lists it on, and the ones an admin put it on
 * (stageHasProduct). `selectable` leaves out the stages that no selector
 * offers (the direct-route "action" type).
 */
export function stagesWithProduct(
  settings: ChannelSettingsLike,
  channelValue: string,
  product: string,
  {selectable = false}: {selectable?: boolean} = {}
): string[] {
  const types: Array<{value: string}> = findChannelObj(channelValue)?.types ?? [];
  return types
    .map((type) => type.value)
    .filter((stage) => !selectable || isContentTypeSelectable(stage))
    .filter((stage) => stageHasProduct(settings, channelValue, stage, product));
}

/**
 * The products that `stage` has: the code's list, then the products an admin
 * put on the stage. Hidden marks do not change it.
 */
export function stageProductCodes(
  settings: ChannelSettingsLike,
  channelValue: string,
  stage: string
): string[] {
  const codes: string[] = productCodesForType(channelValue, stage) ?? [];
  const added = (settings?.shownStageProducts?.[stage] ?? []).filter(
    (code) => !codes.includes(code) && stageHasProduct(settings, channelValue, stage, code)
  );
  return [...codes, ...added];
}

/**
 * The stages that decide whether the Product dropdown offers `product`: each
 * selectable stage of the product on a stage-typed channel, the current type
 * on a content-type channel, and none on a channel without types.
 */
export function productDropdownStages(
  settings: ChannelSettingsLike,
  channelValue: string,
  product: string,
  typeValue: string | null | undefined
): string[] {
  const channelObj = findChannelObj(channelValue);
  if (!channelObj?.types?.length) return [];
  if (channelObj.typesAreStages) {
    return stagesWithProduct(settings, channelValue, product, {selectable: true});
  }
  return typeValue ? [typeValue] : [];
}

/** The products of `codes` that the Product dropdown does not offer. */
export function productsNotOffered(
  settings: ChannelSettingsLike,
  channelValue: string,
  codes: readonly string[],
  typeValue: string | null | undefined
): Set<string> {
  return new Set(
    codes.filter(
      (code) =>
        !isProductOffered(
          settings,
          code,
          productDropdownStages(settings, channelValue, code, typeValue)
        )
    )
  );
}

/**
 * The selectable stages of `product` that code can choose for the user, in
 * declared order: the stages it has (stagesWithProduct) that an admin did not
 * hide it on (offeredStages).
 */
export function offeredSelectableStages(
  settings: ChannelSettingsLike,
  channelValue: string,
  product: string | null | undefined,
  keep?: string | null
): string[] {
  if (!product) return [];
  return offeredStages(
    settings,
    product,
    stagesWithProduct(settings, channelValue, product, {selectable: true}),
    keep
  );
}
